/**
 * Russian formatting the design relies on — the TypeScript half of what
 * `packages/limuzin_core/lib/src/format.dart` does for the two Flutter apps.
 *
 * It is a second copy rather than a shared one because nothing compiles Dart and
 * TypeScript together, and the alternative (a code generator for eleven small
 * functions) would cost more than it saves. The Dart file is the original; this
 * one follows it, function for function, and `format.spec.ts` pins the answers
 * both must give.
 */

/**
 * The space the design sets thousands and the ruble sign apart with, made
 * no-break so «1 890 ₽» cannot wrap mid-number. The design's is a full word
 * space; the narrow one (U+202F) this used to be has no glyph in Manrope, so a
 * fallback font drew it barely there. Written as an escape because it is
 * invisible in a diff.
 */
export const noBreakSpace = '\u00A0';

/** «1 240 ₽» — grouped with {@link noBreakSpace}, and no kopeks: nobody quotes them here. */
export function rubles(kopeks: number): string {
  const whole = Math.round(kopeks / 100);
  // A real minus sign, not a hyphen: this appears next to money in a column.
  const sign = whole < 0 ? '−' : '';
  const digits = Math.abs(whole).toString();
  let out = '';
  for (let i = 0; i < digits.length; i += 1) {
    if (i > 0 && (digits.length - i) % 3 === 0) out += noBreakSpace;
    out += digits[i];
  }
  return `${sign}${out}${noBreakSpace}₽`;
}

/** «поездка / поездки / поездок» — the three-form rule, not a two-form guess. */
export function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

export const rides = (n: number): string => `${n} ${plural(n, 'поездка', 'поездки', 'поездок')}`;
/**
 * «1 минута» / «2 минуты» / «5 минут» — the nominative, for a length of time named in
 * its own right: «Бесплатное ожидание — первые 3 минуты».
 */
export const minutes = (n: number): string => `${n} ${plural(n, 'минута', 'минуты', 'минут')}`;

/**
 * «1 минуту» / «2 минуты» / «5 минут» — the accusative, which is the case the
 * prepositions and verbs these surfaces put in front of a length of time govern:
 * «через 1 минуту», «Поездка заняла 1 минуту».
 *
 * Only the one-form differs from [minutes], which is why the wrong case can ship and
 * read perfectly: every figure but 1, 21, 31 … is the same either way.
 */
export const minutesAccusative = (n: number): string =>
  `${n} ${plural(n, 'минуту', 'минуты', 'минут')}`;
export const drivers = (n: number): string => `${n} ${plural(n, 'водитель', 'водителя', 'водителей')}`;
export const places = (n: number): string => `${n} ${plural(n, 'адрес', 'адреса', 'адресов')}`;
export const times = (n: number): string => `${n} ${plural(n, 'раз', 'раза', 'раз')}`;

const MONTHS = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

/** «Сегодня» / «Вчера» / «7 сентября», the way both apps label a day. */
export function dayLabel(when: Date, now = new Date()): string {
  const date = Date.UTC(when.getFullYear(), when.getMonth(), when.getDate());
  const base = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((date - base) / 86_400_000);
  if (days === 0) return 'Сегодня';
  if (days === 1) return 'Завтра';
  if (days === -1) return 'Вчера';
  return `${when.getDate()} ${MONTHS[when.getMonth()]}`;
}

export function hhmm(when: Date): string {
  return `${String(when.getHours()).padStart(2, '0')}:${String(when.getMinutes()).padStart(2, '0')}`;
}

/** «14:05:09» — the clock to the second, where the order of quick steps matters. */
export function hhmmss(when: Date): string {
  return `${hhmm(when)}:${String(when.getSeconds()).padStart(2, '0')}`;
}

/** «7 сентября, 14:05» — a whole moment, for a ledger row or a submission time. */
export function moment(when: Date, now = new Date()): string {
  return `${dayLabel(when, now)}, ${hhmm(when)}`;
}

/** The same inside a sentence — «создан сегодня, 14:05» — where «Сегодня» would read as a new one. */
export function momentInline(when: Date, now = new Date()): string {
  const text = moment(when, now);
  return text.charAt(0).toLowerCase() + text.slice(1);
}

/** «2 мин» — how long something has been sitting there. */
export function ago(since: Date, now = new Date()): string {
  const seconds = Math.floor((now.getTime() - since.getTime()) / 1000);
  if (seconds < 60) return 'только что';
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins} мин`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} ${plural(hours, 'час', 'часа', 'часов')}`;
  const days = Math.floor(hours / 24);
  return `${days} ${plural(days, 'день', 'дня', 'дней')}`;
}

/** Digits only, then «+7 926 341-08-72» as the phone fields render it. */
export function prettyPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  // Russian only: a foreign number of the same length — +1 555 123 4567 — is not
  // grouped the Russian way, and is shown as it was written instead.
  if (digits.length !== 11 || !/^[78]/.test(digits)) return raw;
  const d = digits.replace(/^8/, '7');
  return `+${d[0]} ${d.slice(1, 4)} ${d.slice(4, 7)}-${d.slice(7, 9)}-${d.slice(9)}`;
}

/**
 * The shape a number takes while it is being typed: «+7 926 341-08-72».
 *
 * The twin of `phoneAsTyped` in `format.dart`. Only the digits survive; the spaces
 * and the dashes are rebuilt around them, so a pasted number and a typed one come
 * out looking the same — like the placeholder standing behind the box.
 *
 * Nobody has to type the country code. A leading 7 or 8 is read as the code itself
 * and written as «+7»; any other first digit is a subscriber digit — a number here
 * starts with 9, and that is what somebody reaching for their own number types
 * first — so «+7 » goes in front of it. Nothing in, nothing out: a box cleared to
 * the last character shows its placeholder again rather than a «+7» that cannot be
 * deleted.
 */
export function phoneAsTyped(raw: string): string {
  // «+» and another country code: let through as typed, digits only. A «+7» is
  // still grouped the Russian way, and so is anything typed without a «+».
  const typed = raw.trimStart();
  if (typed.startsWith('+')) {
    const plus = typed.replace(/\D/g, '');
    if (plus === '') return '+';
    if (plus[0] !== '7') return `+${plus.slice(0, 15)}`;
  }
  const digits = raw.replace(/\D/g, '');
  if (digits === '') return '';
  const rest = digits[0] === '7' || digits[0] === '8' ? digits.slice(1) : digits;
  const national = rest.slice(0, 10);
  let out = '+7';
  for (let i = 0; i < national.length; i += 1) {
    if (i === 0 || i === 3) out += ' ';
    if (i === 6 || i === 8) out += '-';
    out += national[i];
  }
  return out;
}

/**
 * +7 and ten digits, however it was typed — or null. For what has to be Russian:
 * «Перевод на», which a passenger pays through СБП.
 */
export function normaliseRussianPhone(raw: string): string | null {
  let digits = raw.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('8')) digits = `7${digits.slice(1)}`;
  if (digits.length === 10) digits = `7${digits}`;
  if (digits.length !== 11 || !digits.startsWith('7')) return null;
  return `+${digits}`;
}

/**
 * What the API wants for an account: a Russian number however it was typed, or any
 * other country's written with its «+» — eight to fifteen digits, as E.164 allows.
 */
export function normalisePhone(raw: string): string | null {
  const russian = normaliseRussianPhone(raw);
  if (russian) return russian;
  const typed = raw.trim();
  if (!typed.startsWith('+')) return null;
  const digits = typed.replace(/\D/g, '');
  // A «+7» is Russian and answers to Russian rules alone: «+7 900 000-00» is a
  // number half typed, not a nine-digit one from somewhere else.
  if (digits[0] === '7') return null;
  if (digits.length < 8 || digits.length > 15 || digits[0] === '0') return null;
  return `+${digits}`;
}

/** Initials for the round avatars — «ИП» from «Иван Петров». */
export function initials(first?: string | null, last?: string | null): string {
  const a = (first ?? '').trim();
  const b = (last ?? '').trim();
  if (!a && !b) return '—';
  return ((a[0] ?? '') + (b[0] ?? '')).toUpperCase();
}

/**
 * Rubles typed into a box, as the integer kopeks everything server-side speaks.
 *
 * Parsed digit by digit rather than through `Number`: «1234.56» × 100 is
 * 123455.99999999999 in binary floating point, and this is money. Returns null
 * for anything that is not a number, so a caller can refuse rather than send 0.
 */
export function kopeksFromRubles(input: string): number | null {
  const text = input.replace(/[\s  ₽]/g, '').replace(',', '.');
  if (!/^-?\d*(\.\d{0,2})?$/.test(text) || text === '' || text === '-' || text === '.') {
    return null;
  }
  const negative = text.startsWith('-');
  const [whole = '0', fraction = ''] = text.replace('-', '').split('.');
  const kopeks = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
  return negative ? -kopeks : kopeks;
}

/** The other direction, for filling a box from a stored figure: «149.00». */
export function rublesInput(kopeks: number): string {
  const sign = kopeks < 0 ? '-' : '';
  const abs = Math.abs(kopeks);
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}
